import { useState, useEffect } from "react";
import axios from "../api/axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import './styles/LoginSignup.css';
import SEO from "../seo/SEO";
import config from "../config/config";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); 
  const [showPassword, setShowPassword] = useState(false); 
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); 
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [validToken, setValidToken] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setValidToken(false);
      setLoading(false);
      setTimeout(() => navigate("/login"), 3000);
      return;
    }
  
    const verifyToken = async () => {
      try {
        const response = await axios.post("/api/auth/verify-reset-token", { token });
        if (!response.data.valid) {
          setValidToken(false);
          setTimeout(() => navigate("/login"), 3000);
        }
      } catch {
        setValidToken(false);
        setTimeout(() => navigate("/login"), 3000);
      } finally {
        setLoading(false);
      }
    };
  
    verifyToken();
  }, [token, navigate]);
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    // ✅ Password match validation
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      const response = await axios.post("/api/auth/reset-password", { token, email, newPassword: password });
      setMessage(response.data.message);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong.");
    }
  };

  if (loading) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <p className="error">Invalid or expired token. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (

    <>
        <SEO
            robots="noindex, nofollow"
            title="Reset Password - Withrow Knives"
            description="Reset your Withrow Knives account password securely. Enter your new password to regain access to your account."
            url={`${config.metadata.siteUrl}/reset-password`}
        />
        


    
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <h2>Reset Password</h2>
          <p>Return to <Link to="/login">Login</Link></p>

          {/* ✅ Ensure messages are accessible */}
          {message && <p className="success" aria-live="polite">{message}</p>}
          {error && <p className="error" aria-live="polite">{error}</p>}

          <form onSubmit={handleSubmit}>
            {/* ✅ Label for screen readers */}
            <label htmlFor="reset-email" className="sr-only">Enter your email</label>
            <input
              id="reset-email"
              type="email"
              placeholder="Enter your email"
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            {/* ✅ New Password Field with Visibility Toggle */}
            <div className="password-wrapper">
              <label htmlFor="new-password" className="sr-only">Enter new password</label>
              <input
                id="new-password"
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle new password visibility"
              >
                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>

            {/* ✅ Confirm Password Field with Visibility Toggle */}
            <div className="password-wrapper">
              <label htmlFor="confirm-password" className="sr-only">Confirm your new password</label>
              <input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label="Toggle confirm password visibility"
              >
                <i className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>

            <button type="submit">Reset Password</button>
          </form>
        </div>
      </div>

    </>
  );
};

export default ResetPassword;
